import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingBag, Truck, Clock, ShieldCheck } from "lucide-react";
import api, { imgUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import PincodeChecker from "@/components/PincodeChecker";
import DeliveryCountdown from "@/components/DeliveryCountdown";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [weight, setWeight] = useState(null);
  const [message, setMessage] = useState("");
  const { addItem, pincodeInfo } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      const ws = r.data.weight_options || [];
      // Default to "1 Kg" if present, else first, else null
      const preferred = ws.find((w) => w.label === "1 Kg") || ws[0] || null;
      setWeight(preferred);
    });
  }, [id]);

  if (!product) return <div className="max-w-7xl mx-auto px-6 py-24 text-center text-[#6C665F]">Loading product…</div>;

  const price = weight ? weight.price : product.price;

  const addToCart = () => {
    addItem({
      product_id: product.id,
      name: product.name,
      image: product.image,
      price,
      weight: weight ? weight.label : null,
      message: message || null,
      quantity: 1,
    });
    toast.success("Added to your basket");
  };

  const buyNow = () => { addToCart(); navigate("/cart"); };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-16">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-10 md:gap-16">
        <div className="md:col-span-3">
          <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-[#F4F2EC] sticky top-24">
            <img src={imgUrl(product.image)} alt={product.name} className="w-full h-full object-cover" data-testid="product-image" />
          </div>
        </div>
        <div className="md:col-span-2">
          <div className="overline">{product.category}</div>
          <h1 className="font-display text-4xl md:text-5xl mt-2" data-testid="product-name">{product.name}</h1>
          <div className="mt-4 text-2xl font-semibold" data-testid="product-price">₹{price}</div>
          {pincodeInfo?.express && (
            <div className="mt-3"><DeliveryCountdown /></div>
          )}
          <p className="mt-6 text-[#6C665F] leading-relaxed">{product.description}</p>

          {product.weight_options?.length > 0 && (
            <div className="mt-8">
              <div className="overline mb-3">Choose an option</div>
              <div className="flex gap-3 flex-wrap">
                {product.weight_options.map((w) => (
                  <button
                    key={w.label}
                    data-testid={`weight-${w.label.replace(/\s/g, '')}`}
                    onClick={() => setWeight(w)}
                    className={`px-5 py-2 rounded-full border text-sm ${weight?.label === w.label ? "bg-[#2D2A26] text-white border-[#2D2A26]" : "bg-white border-[#E5DFD3]"}`}
                  ><span>{w.label}</span> <span className="opacity-70">· ₹{w.price}</span></button>
                ))}
              </div>
            </div>
          )}

          {product.category === "cakes" && (
            <div className="mt-8">
              <div className="overline mb-3">Personalise (optional)</div>
              <input
                data-testid="personalise-input"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Message on cake e.g. Happy Birthday Aarav"
                className="input-eko"
                maxLength={40}
              />
            </div>
          )}

          <div className="mt-8">
            <PincodeChecker compact />
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={addToCart} data-testid="add-to-cart-btn" className="btn-outline">
              <ShoppingBag className="w-4 h-4 inline mr-2" /> Add to Basket
            </button>
            <button onClick={buyNow} data-testid="buy-now-btn" className="btn-primary">Buy Now</button>
          </div>

          <div className="mt-10 border-t border-[#E5DFD3] pt-6 grid grid-cols-1 gap-3 text-sm text-[#6C665F]">
            <div className="flex items-center gap-2"><Truck className="w-4 h-4 text-[#8C3A3A]" /> Delivered pan-India in your chosen slot</div>
            <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#8C3A3A]" /> Same-day slots available on select pincodes</div>
            <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#8C3A3A]" /> Secure UPI, cards or Cash on Delivery</div>
          </div>
        </div>
      </div>
    </div>
  );
}
